/**
 * Frontend SSE-клиент для real-time чат-событий.
 *
 * Заменяет 700ms polling: один EventSource на /api/chat/stream — события
 * (message/edit/delete/reaction/read/typing) приходят push'ом, патчат
 * react-query кэш в месте. Polling остаётся только как fallback на 30с —
 * на случай если SSE-соединение порвалось и onerror не обнаружил.
 *
 * Singleton: один EventSource на всё приложение. ChatPage делает
 * `startChatStream(qc, currentUserId)` при mount и `stopChatStream()` при
 * unmount. Browser EventSource auto-reconnect'ится при разрывах; на onopen
 * мы invalidate messages-кэш чтобы догнать пропущенные сообщения.
 */
import type { QueryClient } from "@tanstack/react-query";
import type { ChatMessage } from "@/api/dashboard";

type TypingPayload = {
  user_id: string;
  display_name: string;
  avatar_filename: string | null;
  ts: string;
};

let es: EventSource | null = null;
let sharedWorker: SharedWorker | null = null;
let workerPort: MessagePort | null = null;
let openCount = 0;
let qcRef: QueryClient | null = null;
let myUserIdRef: string = "";
let activeTopicIdRef: number | null = null;
let workerWasDisconnected = false;
let resumeTimer: number | null = null;
let resumeHandlersBound = false;

/** Активный топик нужен для catch-up после возврата PWA/TWA из фона. */
export function setActiveTopicForStream(topicId: number | null): void {
  activeTopicIdRef = topicId;
}

/** Pin-jump guard: пока true, applyRead/SSE НЕ инвалидирует chat/messages —
 *  иначе refetch затирает наш replaced slice и Virtuoso уезжает в bottom.
 *  Сетится через setPinJumpActive(topicId, true) из ChatPage перед remount
 *  и снимается setPinJumpActive(topicId, false) когда юзер начинает скроллить. */
const pinJumpActive: Set<number> = new Set();
export function setPinJumpActive(topicId: number, active: boolean): void {
  if (active) pinJumpActive.add(topicId);
  else pinJumpActive.delete(topicId);
}

/** Догнать пропущенные данные после reconnect/resume. Inactive message-query
 *  не трогаем: sidebar уже обновится, а полная история загрузится при открытии.
 *  Pin-jump тоже не сбрасываем, чтобы не увести пользователя со старого сообщения. */
let lastGapInvalidateAt = 0;
function invalidateAfterGap(): void {
  if (!qcRef) return;
  // На возврате в PWA resume-хендлер и onopen переоткрытого EventSource
  // приходят парой → два refetch ленты подряд. Склеиваем в один.
  const now = Date.now();
  if (now - lastGapInvalidateAt < 1500) return;
  lastGapInvalidateAt = now;
  void qcRef.invalidateQueries({ queryKey: ["chat/topics"] });
  const topicId = activeTopicIdRef;
  if (topicId != null && !pinJumpActive.has(topicId)) {
    void qcRef.invalidateQueries({ queryKey: ["chat/messages", topicId] });
  }
}

/** iOS/Android webview может разморозить JS без надёжного focus-event и без
 *  немедленного EventSource.onopen. pageshow + visibilitychange + online
 *  покрывают возврат TWA/PWA и восстановление сети; короткий debounce склеивает
 *  несколько событий одного resume в один refetch. */
function scheduleResumeCatchUp(): void {
  if (typeof window === "undefined" || typeof document === "undefined") return;
  if (document.visibilityState === "hidden") return;
  if (resumeTimer != null) window.clearTimeout(resumeTimer);
  resumeTimer = window.setTimeout(() => {
    resumeTimer = null;
    invalidateAfterGap();
  }, 120);
}

function onVisibilityChange(): void {
  if (document.visibilityState === "visible") scheduleResumeCatchUp();
}

/** Первый pageshow обычной загрузки (persisted=false) — не «возврат»: список и
 *  лента только что загружены, а catch-up давал второй /api/chat/topics и
 *  повторную страницу сообщений прямо на старте. Возврат из bfcache приходит
 *  с persisted=true и обрабатывается как раньше. */
function onPageShow(e: PageTransitionEvent): void {
  if (!e.persisted) return;
  scheduleResumeCatchUp();
}

function bindResumeHandlers(): void {
  if (resumeHandlersBound || typeof window === "undefined") return;
  document.addEventListener("visibilitychange", onVisibilityChange);
  window.addEventListener("pageshow", onPageShow);
  window.addEventListener("online", scheduleResumeCatchUp);
  resumeHandlersBound = true;
}

function unbindResumeHandlers(): void {
  if (!resumeHandlersBound || typeof window === "undefined") return;
  document.removeEventListener("visibilitychange", onVisibilityChange);
  window.removeEventListener("pageshow", onPageShow);
  window.removeEventListener("online", scheduleResumeCatchUp);
  if (resumeTimer != null) window.clearTimeout(resumeTimer);
  resumeTimer = null;
  resumeHandlersBound = false;
}

function apiBase(): string {
  const v = (import.meta as any).env?.VITE_API_BASE;
  return typeof v === "string" && v ? v : "/api";
}

/** SharedWorker недоступен на iOS Safari Standalone (PWA-режим) — fall back
 *  на per-tab EventSource. На desktop Chrome/Firefox/Edge — поддерживается. */
function isSharedWorkerSupported(): boolean {
  if (typeof (globalThis as any).SharedWorker === "undefined") return false;
  const ua = navigator.userAgent;
  const isIOS = /iPhone|iPad|iPod/.test(ua) || (/Mac/.test(ua) && "ontouchend" in document);
  const isStandalone =
    (navigator as any).standalone === true ||
    (typeof matchMedia === "function" && matchMedia("(display-mode: standalone)").matches);
  if (isIOS && isStandalone) return false;
  return true;
}

/** Безопасный JSON.parse — на трэшевом event просто игнорим без падения. */
function parse(ev: MessageEvent | { data: any } | null): any | null {
  if (!ev) return null;
  const raw = (ev as any).data;
  try { return typeof raw === "string" ? JSON.parse(raw) : raw; } catch { return null; }
}

function applyMessageInsert(topicId: number, msg: ChatMessage) {
  if (!qcRef) return;
  qcRef.setQueryData<ChatMessage[]>(["chat/messages", topicId], (old) => {
    // Не создавать «полный» кэш истории из одного SSE-сообщения. Если топик
    // ещё ни разу не открывали, setQueryData([msg]) помечал singleton-cache
    // свежим на 60с, и useQuery при открытии НЕ запрашивал последние 200
    // сообщений. Результат — пустой диалог с единственным новым сообщением до
    // ручного refresh. Для уже загруженного (включая []) кэша патч безопасен.
    if (!old) return old;
    if (old.some((m) => m.id === msg.id)) return old;
    let replaced = false;
    const out = old.map((m) => {
      if (!replaced && m.id < 0) { replaced = true; return msg; }
      return m;
    });
    if (!replaced) out.push(msg);
    return out;
  });
  // Sidebar: вместо invalidateQueries (= refetch всего списка + cascade re-render
  // ChatPage → Virtuoso → visible jitter) делаем OPTIMISTIC setQueryData.
  // Patch только нужные поля топика: last_message_text, last_message_at,
  // unread_count (если от чужого юзера).
  // Patch только реальные поля ChatTopic: last_message (object) + unread_count.
  // Раньше я patch'ил несуществующие last_message_text/at — sidebar показывал
  // старое сообщение пока не открыл чат.
  qcRef.setQueryData<Array<Record<string, unknown>>>(["chat/topics"], (topics) => {
    if (!topics) return topics;
    const isMine = msg.author_user_id === myUserIdRef;
    return topics.map(t => {
      if (t.id !== topicId) return t;
      const preview = (msg.text || "").trim() || (msg.attachments?.length ? "📎 файл" : "");
      return {
        ...t,
        last_message: {
          id: msg.id,
          preview,
          author: msg.author_name,
          ts: msg.created_at,
          from_me: isMine,
          read_by_others: false,
        },
        unread_count: isMine ? t.unread_count : ((t.unread_count as number) || 0) + 1,
      };
    });
  });
}

function applyMessageEdit(topicId: number, patch: { id: number; text?: string; edited_at?: string | null; meta?: any }) {
  if (!qcRef) return;
  qcRef.setQueryData<ChatMessage[]>(["chat/messages", topicId], (old) =>
    old ? old.map((m) => {
      if (m.id !== patch.id) return m;
      const next: any = { ...m };
      // Partial patch — apply только присутствующие поля. Это позволяет
      // отдельно publish'ить text-edits (из edit_message) и async meta-edits
      // (link_preview-ready, future: video_thumbnail-ready) без затирания.
      if (Object.prototype.hasOwnProperty.call(patch, "text")) next.text = patch.text;
      if (Object.prototype.hasOwnProperty.call(patch, "edited_at")) next.edited_at = patch.edited_at;
      if (Object.prototype.hasOwnProperty.call(patch, "meta")) next.meta = patch.meta;
      return next;
    }) : old,
  );
}

function applyMessageDelete(topicId: number, patch: { id: number; deleted_at: string }) {
  if (!qcRef) return;
  qcRef.setQueryData<ChatMessage[]>(["chat/messages", topicId], (old) =>
    old ? old.map((m) => (m.id === patch.id ? { ...m, deleted_at: patch.deleted_at, text: "" } : m)) : old,
  );
}

function applyReaction(topicId: number, patch: { message_id: number; reactions: any[] }) {
  if (!qcRef) return;
  const me = myUserIdRef;
  qcRef.setQueryData<ChatMessage[]>(["chat/messages", topicId], (old) => {
    if (!old) return old;
    return old.map((m) => {
      if (m.id !== patch.message_id) return m;
      // mine пересчитываем тут — backend не знает кому шлёт
      const reactions = patch.reactions.map((r) => ({
        emoji: r.emoji,
        count: r.count,
        mine: !!(me && (r.users || []).some((u: any) => u.user_id === me)),
        users: r.users || [],
      }));
      return { ...m, reactions };
    });
  });
}

function applyRead(
  topicId: number,
  patch: { user_id: string; last_read_id: number; display_name?: string; avatar_filename?: string | null },
) {
  if (!qcRef) return;
  // Точечный патч read_by (как applyReaction) ВМЕСТО invalidate → refetch всего
  // слайса 200 сообщений на КАЖДОЕ прочтение. В активном чате, где люди читают
  // постоянно, это перекачивало ленту каждые пару секунд. Читатель прочитал всё
  // с id <= last_read_id → добавляем его UserMini в read_by этих сообщений
  // (кроме им же написанных — автор исключён из read_by, как на бэкенде).
  // Патч не меняет длину/порядок массива → Virtuoso не дёргается (pin-jump-safe).
  const reader = {
    user_id: patch.user_id,
    display_name: patch.display_name || "",
    avatar_filename: patch.avatar_filename ?? null,
  };
  qcRef.setQueryData<ChatMessage[]>(["chat/messages", topicId], (old) => {
    if (!old) return old;
    let changed = false;
    const next = old.map((m) => {
      if (m.id > patch.last_read_id) return m;          // ещё не прочитано этим юзером
      if (m.author_user_id === patch.user_id) return m; // автор исключён из своего read_by
      const rb = m.read_by || [];
      if (rb.some((u) => u.user_id === patch.user_id)) return m;  // уже отмечен
      changed = true;
      return { ...m, read_by: [...rb, reader] };
    });
    return changed ? next : old;
  });
}

function applyTyping(topicId: number, payload: TypingPayload) {
  if (!qcRef) return;
  // Safety-net: backend для public-топиков шлёт typing ВСЕМ (broker audience
  // = None для public). Для DM/private_group уже фильтрует sender'а в
  // post_typing. Здесь дополнительно отсеиваем СВОЙ typing-event —
  // иначе в шапке у нас «Я печатает...».
  if (payload.user_id === myUserIdRef) return;
  // По-топику: добавляем/обновляем пользователя. TTL 6с — клиент дропает.
  qcRef.setQueryData<TypingPayload[]>(["chat/typing", topicId], (old) => {
    const list = (old || []).filter((u) => u.user_id !== payload.user_id);
    list.push(payload);
    return list;
  });
  // Bulk-эндпоинт /chat/typing — тоже патчим (тот же payload в bucket topicId)
  qcRef.setQueryData<Record<number, TypingPayload[]>>(["chat/typing-all"], (old) => {
    const map = { ...(old || {}) };
    const bucket = (map[topicId] || []).filter((u) => u.user_id !== payload.user_id);
    bucket.push(payload);
    map[topicId] = bucket;
    return map;
  });
  // Сброс через 6с (TG ~6c TTL) — простой setTimeout, никаких очередей
  window.setTimeout(() => {
    if (!qcRef) return;
    qcRef.setQueryData<TypingPayload[]>(["chat/typing", topicId], (old) =>
      (old || []).filter((u) => u.user_id !== payload.user_id || u.ts !== payload.ts),
    );
    qcRef.setQueryData<Record<number, TypingPayload[]>>(["chat/typing-all"], (old) => {
      if (!old) return old;
      const bucket = (old[topicId] || []).filter((u) => u.user_id !== payload.user_id || u.ts !== payload.ts);
      const map = { ...old };
      if (bucket.length === 0) delete map[topicId];
      else map[topicId] = bucket;
      return map;
    });
  }, 6000);
}

/** Единый dispatcher — один путь для EventSource и SharedWorker. */
function dispatchEvent(type: string, rawData: any): void {
  if (type === "replay_unavailable") {
    if (qcRef) qcRef.invalidateQueries({ queryKey: ["chat/messages"] });
    return;
  }
  const p = parse({ data: rawData });
  if (!p) return;
  switch (type) {
    case "message":
      if (p.topic_id && p.data) applyMessageInsert(p.topic_id, p.data as ChatMessage);
      break;
    case "message_edit":
      if (p.topic_id) applyMessageEdit(p.topic_id, p.data);
      break;
    case "message_delete":
      if (p.topic_id) applyMessageDelete(p.topic_id, p.data);
      break;
    case "reaction":
      if (p.topic_id) applyReaction(p.topic_id, p.data);
      break;
    case "read":
      if (p.topic_id) applyRead(p.topic_id, p.data);
      break;
    case "typing":
      if (p.topic_id) applyTyping(p.topic_id, p.data as TypingPayload);
      break;
  }
}

function bindHandlers(e: EventSource) {
  for (const t of [
    "message", "message_edit", "message_delete",
    "reaction", "read", "typing", "replay_unavailable",
  ]) {
    e.addEventListener(t, (ev) => dispatchEvent(t, (ev as MessageEvent).data));
  }
}

export function startChatStream(qc: QueryClient, currentUserId: string): void {
  qcRef = qc;
  myUserIdRef = currentUserId;
  openCount += 1;
  bindResumeHandlers();
  if (es || workerPort) return;  // уже подключены

  if (isSharedWorkerSupported()) {
    try {
      sharedWorker = new SharedWorker("/sse-shared-worker.js");
      workerPort = sharedWorker.port;
      workerPort.start();
      workerPort.postMessage({ type: "init", apiBase: apiBase() });
      workerPort.addEventListener("message", (ev) => {
        const m = (ev as MessageEvent).data;
        if (!m || typeof m !== "object") return;
        if (m.kind === "event" && typeof m.type === "string") {
          dispatchEvent(m.type, m.data);
        } else if (m.kind === "error" || m.kind === "fatal") {
          workerWasDisconnected = true;
        } else if (m.kind === "open") {
          if (workerWasDisconnected) invalidateAfterGap();
          workerWasDisconnected = false;
        }
      });
      return;
    } catch (e) {
      // SharedWorker отказал — fallback на EventSource
      sharedWorker = null;
      workerPort = null;
    }
  }

  // iOS PWA fallback: per-tab EventSource. Браузер сам отправляет Last-Event-ID
  // на reconnect; backend replay'ит пропущенное (P1#4). При overflow buffer'а
  // — handler 'replay_unavailable' делает invalidate (см. dispatchEvent).
  const url = `${apiBase()}/chat/stream`;
  es = new EventSource(url, { withCredentials: true });
  let wasDisconnected = false;
  es.onopen = () => {
    if (wasDisconnected) {
      // После reconnect: Last-Event-ID replay может НЕ покрыть всё (buffer
      // overflow на backend). Force-invalidate чтобы догнать unread updates,
      // pending pinned changes и т.п.
      invalidateAfterGap();
    }
    wasDisconnected = false;
  };
  es.onerror = () => {
    // EventSource сам retry'ится (exponential backoff). Mark — onopen
    // среагирует invalidate'ом критичных queries после восстановления.
    wasDisconnected = true;
  };
  bindHandlers(es);
}

export function stopChatStream(): void {
  openCount = Math.max(0, openCount - 1);
  if (openCount > 0) return;
  unbindResumeHandlers();
  if (es) {
    try { es.close(); } catch { /* ignore */ }
    es = null;
  }
  if (workerPort) {
    try { workerPort.postMessage({ type: "close" }); } catch { /* ignore */ }
    try { workerPort.close(); } catch { /* ignore */ }
    workerPort = null;
    sharedWorker = null;
  }
  qcRef = null;
  activeTopicIdRef = null;
  workerWasDisconnected = false;
}
